import { createApi } from '@reduxjs/toolkit/query/react';
import { CreateNotificationDto, IPagedCollection, NotificationDto, NotificationsInfoDto } from '@sr/dto';
import { NOTIFICATIONS_BASE_URL } from 'shared/api/api';
import { createBaseQuery } from 'shared/api/rtk-query';
import { acquireRealtimeConnection } from 'shared/api/realtime';
import { sendBrowserNotification } from './system-notification';

export const api = createApi({
  reducerPath: 'notifications',
  baseQuery: createBaseQuery(NOTIFICATIONS_BASE_URL),
  endpoints: (builder) => ({
    getManagerNotifications: builder.query<NotificationsInfoDto, number>({
      query: (managerId) => `latest-unreaded/${managerId}`,
      async onCacheEntryAdded(arg, { updateCachedData, cacheDataLoaded, cacheEntryRemoved }) {
        const { socket: client, release } = acquireRealtimeConnection();
        const onNotification = async (data: NotificationDto) => {
          updateCachedData((draft) => ({ count: draft.count + 1, items: [data, ...draft.items] }));
          await sendBrowserNotification('Solution 2.0', data.message);
        };
        try {
          await cacheDataLoaded;

          client.on('notification', onNotification);
        } catch (e) {
          console.error(`notificationsApi.onCacheEntryAdded: ${e}`);
        }

        await cacheEntryRemoved;

        client.off('notification', onNotification);
        release();
      }
    }),
    getNotifications: builder.query<IPagedCollection<NotificationDto>, { managerId: number; filter: string }>({
      query: (args) => ({
        url: `manager/${args.managerId}?${args.filter}`,
        method: 'GET'
      })
    }),
    addNotification: builder.mutation<NotificationDto, CreateNotificationDto>({
      query: (notification) => ({
        url: '',
        method: 'POST',
        body: notification
      })
    }),
    markReadedAll: builder.mutation<void, number>({
      query: (managerId: number) => ({ url:'/markReadedAll', method: 'PATCH' }),
      onQueryStarted: async (managerId, { dispatch, queryFulfilled }) => {
        const patchResult = dispatch(notificationsApi.util.updateQueryData('getManagerNotifications', managerId, (draft) => {
          draft.items.splice(0, draft.items.length);
          draft.count = 0;
        }));

        try {
          await queryFulfilled;
        } catch (e) {
          patchResult.undo();
          console.error('RTK Query onQueryStarted error', e);
        }
      }
    }),
    setNotificationRead: builder.mutation<void, { message: string }>({
      query: ({ message }) => ({
        url: '/notifications/set-read',
        method: 'POST',
        body: { message }
      })
    })
  })
});

export const notificationsApi = api.enhanceEndpoints({
  addTagTypes: ['NOTIFICATIONS'],
  endpoints: {
    getNotifications: {
      providesTags: ['NOTIFICATIONS']
    },
    markReadedAll: {
      invalidatesTags: ['NOTIFICATIONS']
    },
    addNotification: {
      invalidatesTags: ['NOTIFICATIONS']
    }
  }
});
